import axios from 'axios';

// Detect if we're on a custom domain (not preview.emergentagent.com)
const isCustomDomain = typeof window !== 'undefined' && 
  !window.location.hostname.includes('preview.emergentagent.com') &&
  !window.location.hostname.includes('localhost');

// For custom domains, ALWAYS use relative URLs
// For preview/localhost, use the env variable
const BACKEND_URL = isCustomDomain ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

console.log('[API] Is Custom Domain:', isCustomDomain);
console.log('[API] API Base URL:', API_BASE);

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to attach JWT token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/token', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
};

// Users API
export const usersAPI = {
  getAll: () => apiClient.get('/users'),
  getById: (id) => apiClient.get(`/users/${id}`),
};

// Alerts API
export const alertsAPI = {
  getAll: (params) => apiClient.get('/alerts', { params }),
  getById: (id) => apiClient.get(`/alerts/${id}`),
  create: (data) => apiClient.post('/alerts', data),
  update: (id, data) => apiClient.patch(`/alerts/${id}`, data),
  delete: (id) => apiClient.delete(`/alerts/${id}`),
};

// Assets API
export const assetsAPI = {
  getAll: () => apiClient.get('/assets'),
  getById: (id) => apiClient.get(`/assets/${id}`),
  create: (data) => apiClient.post('/assets', data),
  update: (id, data) => apiClient.patch(`/assets/${id}`, data),
  delete: (id) => apiClient.delete(`/assets/${id}`),
};

// Areas API
export const areasAPI = {
  getAll: () => apiClient.get('/areas'),
  getById: (id) => apiClient.get(`/areas/${id}`),
  create: (data) => apiClient.post('/areas', data),
  update: (id, data) => apiClient.patch(`/areas/${id}`, data),
  delete: (id) => apiClient.delete(`/areas/${id}`),
};

// Schemes API
export const schemesAPI = {
  getAll: () => apiClient.get('/schemes'),
  getById: (id) => apiClient.get(`/schemes/${id}`),
  create: (data) => apiClient.post('/schemes', data),
  update: (id, data) => apiClient.patch(`/schemes/${id}`, data),
  delete: (id) => apiClient.delete(`/schemes/${id}`),
};

// Reports API
export const reportsAPI = {
  getNRW: () => apiClient.get('/reports/nrw'),
  createNRW: (data) => apiClient.post('/reports/nrw', data),
  updateNRW: (id, data) => apiClient.patch(`/reports/nrw/${id}`, data),
  getConsumption: (params) => apiClient.get('/reports/consumption', { params }),
  getPerformance: () => apiClient.get('/reports/performance'),
};

// GIS API
export const gisAPI = {
  upload: (formData) => apiClient.post('/gis/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// Communications API
export const communicationsAPI = {
  getAll: () => apiClient.get('/communications'),
  create: (data) => apiClient.post('/communications', data),
};

// Evidence API
export const evidenceAPI = {
  getAll: () => apiClient.get('/evidence'),
};

// GIS Layers API
export const gisLayersAPI = {
  getAll: () => apiClient.get('/gis-layers'),
  create: (name, formData) => apiClient.post(`/gis-layers?name=${encodeURIComponent(name)}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id) => apiClient.delete(`/gis-layers/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getKPIs: () => apiClient.get('/dashboard/kpis'),
  getTrends: () => apiClient.get('/dashboard/trends'),
  getCriticalFeed: (limit = 5) => apiClient.get(`/dashboard/critical-feed?limit=${limit}`),
};

// Sensor Data API
export const sensorDataAPI = {
  getAll: (params) => apiClient.get('/sensor-data', { params }),
  create: (data) => apiClient.post('/sensor-data', data),
};

// Key Personnel API
export const keyPersonnelAPI = {
  getAll: () => apiClient.get('/key-personnel'),
  create: (data) => apiClient.post('/key-personnel', data),
  update: (id, data) => apiClient.patch(`/key-personnel/${id}`, data),
  delete: (id) => apiClient.delete(`/key-personnel/${id}`),
};

// ILK Detection API
export const ilkAPI = {
  getAll: (params) => apiClient.get('/ilk-detections', { params }),
  create: (data) => apiClient.post('/ilk-detections', data),
  isolate: (ilkId, valveId) => apiClient.post(`/ilk-detections/${ilkId}/isolate`, { valve_id: valveId }),
  deployTeam: (ilkId, teamId) => apiClient.post(`/ilk-detections/${ilkId}/deploy-team`, { team_id: teamId }),
  update: (ilkId, data) => apiClient.patch(`/ilk-detections/${ilkId}`, data),
  getStats: () => apiClient.get('/ilk-detections/stats'),
};

// Valve Control API
export const valveAPI = {
  getAll: (params) => apiClient.get('/valves', { params }),
  create: (data) => apiClient.post('/valves', data),
  operate: (operation) => apiClient.post('/valves/operate', operation),
  batchOperate: (valveIds, action, reason) => apiClient.post('/valves/batch-operate', { valve_ids: valveIds, action, reason }),
  getStats: () => apiClient.get('/valves/stats'),
  getDetail: (valveId) => apiClient.get(`/valves/${valveId}`),
};

// Billing & Revenue API
export const billingAPI = {
  getStats: () => apiClient.get('/billing/stats'),
  getConsumers: (params) => apiClient.get('/consumers', { params }),
  getBills: (params) => apiClient.get('/bills', { params }),
  getPayments: (params) => apiClient.get('/payments', { params }),
  getRevenueLeaks: (params) => apiClient.get('/revenue-leaks', { params }),
};

// AI Assistant API
export const aiAPI = {
  chat: (message, sessionId) => apiClient.post('/ai/chat', { message, session_id: sessionId }),
  getHistory: (sessionId) => apiClient.get(`/ai/history/${sessionId}`),
};

// Export all APIs and the base client
export default {
  auth: authAPI,
  users: usersAPI,
  alerts: alertsAPI,
  assets: assetsAPI,
  areas: areasAPI,
  schemes: schemesAPI,
  reports: reportsAPI,
  gis: gisAPI,
  communications: communicationsAPI,
  evidence: evidenceAPI,
  gisLayers: gisLayersAPI,
  dashboard: dashboardAPI,
  sensorData: sensorDataAPI,
  keyPersonnel: keyPersonnelAPI,
  ilk: ilkAPI,
  valves: valveAPI,
  billing: billingAPI,
  ai: aiAPI,
  client: apiClient,
};
